'use client';

import { useEffect, useState } from 'react';

/** Smooth scroll to a [data-toc-id] section, −80px offset, motion-gated. */
export const jumpTo = (tocId: string) => {
  const el = document.querySelector(`[data-toc-id="${tocId}"]`);
  if (!el) return;
  const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  window.scrollTo({
    top: el.getBoundingClientRect().top + window.scrollY - 80,
    behavior: smooth ? 'smooth' : 'auto',
  });
};

/**
 * "ON THIS PAGE" rail: 2px left-rule buttons; the active section is tracked
 * with an IntersectionObserver over the page's [data-toc-id] sections.
 */
export default function TocRail({ sections }: { sections: { id: string; label: string }[] }) {
  const [active, setActive] = useState<string | null>(null);

  useEffect(() => {
    const els = [...document.querySelectorAll('[data-toc-id]')];
    if (!els.length) return;
    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length) setActive(visible[0].target.getAttribute('data-toc-id'));
      },
      { rootMargin: '-90px 0px -65% 0px' },
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [sections]);

  return (
    <aside className="sticky top-[88px] self-start max-[999px]:hidden">
      <span className="mb-2 block font-jetbrains text-[9.5px] tracking-[0.14em] text-dim">
        ON THIS PAGE
      </span>
      <div className="flex flex-col">
        {sections.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => jumpTo(s.id)}
            className={`border-l-2 py-[5px] pl-3 text-left text-[12.5px] transition-colors hover:text-ink ${
              active === s.id ? 'border-brand-600 text-ink' : 'border-line-2 text-muted'
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>
    </aside>
  );
}
